import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { Save } from 'lucide-react';

const DSAPartnerProgramOverviewPage = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [overviewId, setOverviewId] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        is_active: true
    });

    const loanType = 'dsa_partner_program';

    useEffect(() => {
        fetchOverview();
    }, []);

    const fetchOverview = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`/api/admin/loan-overview/${loanType}`);
            if (response.data.success && response.data.data) {
                setOverviewId(response.data.data.id);
                setFormData({
                    title: response.data.data.title || '',
                    description: response.data.data.description || '',
                    is_active: response.data.data.is_active
                });
            }
        } catch (error) {
            console.error('Error fetching overview:', error);
            // Don't show error if it's just not found (first time)
            if (error.response?.status !== 404) {
                toast.error('Failed to load overview data');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        try {
            if (overviewId) {
                await axios.put(`/api/admin/loan-overview/${overviewId}`, {
                    ...formData,
                    loan_type: loanType
                });
                toast.success('Overview updated successfully');
            } else {
                const response = await axios.post('/api/admin/loan-overview', {
                    ...formData,
                    loan_type: loanType
                });
                if (response.data.success && response.data.data) {
                    setOverviewId(response.data.data.id);
                }
                toast.success('Overview created successfully');
            }
        } catch (error) {
            console.error('Error saving overview:', error);
            toast.error('Failed to save overview');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="animate-pulse space-y-4">
                <div className="h-8 bg-slate-200 rounded w-1/4"></div>
                <div className="h-64 bg-slate-200 rounded-xl"></div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-slate-800">Partner Us - Overview</h1>
                <p className="text-slate-500 mt-1">Manage the introduction section of the Partner Us page</p>
            </div>

            <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 space-y-6">
                {/* Title (Hidden/Internal use mostly for this page type, but kept for consistency) */}
                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Internal Title <span className="text-slate-400 font-normal">(Not displayed on page)</span>
                    </label>
                    <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                        placeholder="e.g., Partner Program Introduction"
                    />
                </div>

                {/* Description */}
                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Introduction Text <span className="text-red-500">*</span>
                    </label>
                    <p className="text-xs text-slate-500 mb-2">
                        This text appears below the banner. You can use HTML tags like &lt;span class="font-bold"&gt; for styling.
                    </p>
                    <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        required
                        rows={6}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                        placeholder="Enter the introduction text..."
                    />
                </div>

                {/* Active Status */}
                <div>
                    <label className="flex items-center gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            name="is_active"
                            checked={formData.is_active}
                            onChange={handleChange}
                            className="w-5 h-5 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                        />
                        <span className="text-sm font-semibold text-slate-700">Section Visible</span>
                    </label>
                </div>

                {/* Submit Button */}
                <div className="pt-4 border-t border-slate-100 flex justify-end">
                    <button
                        type="submit"
                        disabled={saving}
                        className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-xl font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                    >
                        <Save size={20} />
                        {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default DSAPartnerProgramOverviewPage;
